/**
 * Infinite Scroll for Collection Pages
 * Loads more products automatically as user scrolls
 */

class InfiniteScroll {
  constructor() {
    this.productGrid = document.querySelector('#product-grid');
    this.loadingIndicator = document.querySelector('#infinite-scroll-loading');
    this.endMessage = document.querySelector('#infinite-scroll-end');

    if (!this.productGrid) {
      return;
    }

    if (!this.loadingIndicator || !this.endMessage) {
      return;
    }

    this.isLoading = false;
    this.isEnabled = true;
    this.isFetching = false;
    this.threshold = 800;
    this.fetchTimeout = 15000;
    this.loadedProductIds = new Set();
    this.abortController = null;

    const urlParams = new URLSearchParams(window.location.search);
    this.currentPage = parseInt(urlParams.get('page')) || 1;

    this.trackInitialProducts();

    this.extractPaginationInfo();

    if (this.hasMorePages) {
      this.attachScrollListener();
      this.hidePagination();
    }

    this.setupFacetListeners();
    this.setupBrowserNavigation();
  }

  /**
   * Track initially loaded products to prevent duplicates
   */
  trackInitialProducts() {
    const products = this.productGrid.querySelectorAll('[data-product-id]');

    products.forEach((product) => {
      const productCard = product.querySelector('[data-product-id]');
      const productId = productCard ? productCard.getAttribute('data-product-id') : null;

      if (productId) {
        this.loadedProductIds.add(productId);
      }
    });
  }

  /**
   * Extract pagination info
   */
  extractPaginationInfo() {
    this.paginationWrapper = document.querySelector('.pagination-wrapper');
    const paginationLinks = document.querySelectorAll('.pagination__item[href]');

    if (paginationLinks.length === 0) {
      this.totalPages = 1;
      this.hasMorePages = false;
      return;
    }

    let maxPage = this.currentPage;
    paginationLinks.forEach((link) => {
      try {
        const url = new URL(link.href, window.location.origin);
        const page = parseInt(url.searchParams.get('page')) || 1;
        if (page > maxPage) maxPage = page;
      } catch (e) {
        console.error('Error extracting pagination info', e);
      }
    });

    this.totalPages = maxPage;
    this.hasMorePages = this.currentPage < this.totalPages;
  }

  /**
   * Hide pagination
   */
  hidePagination() {
    if (this.paginationWrapper) {
      this.paginationWrapper.style.display = 'none';
    }
  }

  /**
   * Show pagination (for fallback)
   */
  showPagination() {
    if (this.paginationWrapper) {
      this.paginationWrapper.style.display = '';
    }
  }

  /**
   * Attach scroll listener
   */
  attachScrollListener() {
    this.handleScroll = this.handleScroll.bind(this);
    this.scrollListener = this.debounce(this.handleScroll, 100);
    window.addEventListener('scroll', this.scrollListener, { passive: true });
  }

  /**
   * Detach scroll listener
   */
  detachScrollListener() {
    if (this.scrollListener) {
      window.removeEventListener('scroll', this.scrollListener);
    }
  }

  /**
   * Handle scroll
   */
  handleScroll() {
    if (!this.isEnabled || this.isLoading || this.isFetching || !this.hasMorePages) {
      return;
    }

    if (!document.body.contains(this.productGrid)) {
      return;
    }

    const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
    const windowHeight = window.innerHeight;

    const gridBottom = this.productGrid.getBoundingClientRect().bottom + scrollTop;
    const scrollBottom = scrollTop + windowHeight;
    const distanceFromGridBottom = gridBottom - scrollBottom;

    if (distanceFromGridBottom < this.threshold) {
      this.loadNextPage();
    }
  }

  /**
   * Load next page of products with timeout
   */
  async loadNextPage() {
    if (this.isLoading || this.isFetching || !this.hasMorePages) {
      return;
    }

    this.isLoading = true;
    this.isFetching = true;
    this.showLoading();

    const nextPage = this.currentPage + 1;

    this.abortController = new AbortController();
    const timeoutId = setTimeout(() => this.abortController.abort(), this.fetchTimeout);

    try {
      const url = new URL(window.location.href);
      url.searchParams.set('page', nextPage);

      const response = await fetch(url.toString(), {
        signal: this.abortController.signal,
      });

      clearTimeout(timeoutId);

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      const html = await response.text();

      const parser = new DOMParser();
      const doc = parser.parseFromString(html, 'text/html');

      const newProducts = doc.querySelectorAll('#product-grid > li');

      if (newProducts.length > 0) {
        if (!document.body.contains(this.productGrid)) {
          this.isFetching = false;
          return;
        }

        this.appendProducts(newProducts);
        this.currentPage = nextPage;
        this.hasMorePages = nextPage < this.totalPages;

        window.history.replaceState({ page: nextPage }, '', url.toString());
        this.reinitializeDynamicFeatures();
      } else {
        this.hasMorePages = false;
      }
    } catch (error) {
      clearTimeout(timeoutId);

      if (error.name === 'AbortError') {
        this.showError('Tempo esgotado ao carregar produtos');
      } else {
        this.showError('Erro ao carregar mais produtos');
      }
    } finally {
      this.isLoading = false;
      this.isFetching = false;
      this.hideLoading();
      this.abortController = null;
    }
  }

  /**
   * Append new products to grid with duplicate prevention and hidden product filtering
   */
  appendProducts(products) {
    let appendedCount = 0;

    products.forEach((product) => {
      const isHidden = product.querySelector('[data-product-hidden="true"]');
      if (isHidden) {
        return;
      }

      const productCard = product.querySelector('[data-product-id]');
      const productId = productCard ? productCard.getAttribute('data-product-id') : null;

      if (productId && this.loadedProductIds.has(productId)) {
        return;
      }

      if (!document.body.contains(this.productGrid)) {
        return;
      }

      const clone = product.cloneNode(true);

      clone.classList.add('infinite-scroll-loaded');
      clone.style.animationDelay = `${appendedCount * 0.02}s`;

      this.productGrid.appendChild(clone);

      if (productId) {
        this.loadedProductIds.add(productId);
      }

      appendedCount++;
    });
  }

  /**
   * Re-initialize dynamic features
   */
  reinitializeDynamicFeatures() {
    if (typeof window.Shopify !== 'undefined') {
      const quickAddButtons = this.productGrid.querySelectorAll('quick-add-button');
      quickAddButtons.forEach((button) => {
        if (button && typeof button.init === 'function') {
          button.init();
        }
      });
    }

    if (typeof window.ScrollAnimation !== 'undefined') {
      window.ScrollAnimation.triggerAnimations();
    }
  }

  /**
   * Setup listeners for filter/sort with MutationObserver
   */
  setupFacetListeners() {
    document.addEventListener('facet-updated', () => {
      this.reset();
    });

    const productGridContainer = document.getElementById('ProductGridContainer');
    if (productGridContainer) {
      this.gridObserver = new MutationObserver((mutations) => {
        const gridReplaced = mutations.some((mutation) => {
          return Array.from(mutation.removedNodes).some(
            (node) => node.id === 'product-grid' || (node.querySelector && node.querySelector('#product-grid'))
          );
        });

        if (gridReplaced) {
          this.reset();
        }
      });

      this.gridObserver.observe(productGridContainer, {
        childList: true,
        subtree: false,
      });
    }
  }

  /**
   * Setup browser navigation (back/forward) handling
   */
  setupBrowserNavigation() {
    window.addEventListener('popstate', (event) => {
      if (event.state?.page) {
        this.currentPage = event.state.page;
        this.isEnabled = false;
      }
    });
  }

  /**
   * Reconnect DOM references after facet updates using MutationObserver
   */
  reconnectDOM() {
    return new Promise((resolve) => {
      this.loadingIndicator = document.querySelector('#infinite-scroll-loading');
      this.endMessage = document.querySelector('#infinite-scroll-end');
      this.productGrid = document.querySelector('#product-grid');
      this.paginationWrapper = document.querySelector('.pagination-wrapper');

      if (this.loadingIndicator && this.endMessage && this.productGrid) {
        resolve(true);
        return;
      }

      const container = document.getElementById('ProductGridContainer');
      if (!container) {
        resolve(false);
        return;
      }

      const observer = new MutationObserver(() => {
        this.loadingIndicator = document.querySelector('#infinite-scroll-loading');
        this.endMessage = document.querySelector('#infinite-scroll-end');
        this.productGrid = document.querySelector('#product-grid');
        this.paginationWrapper = document.querySelector('.pagination-wrapper');

        if (this.loadingIndicator && this.endMessage && this.productGrid) {
          observer.disconnect();
          resolve(true);
        }
      });

      observer.observe(container, {
        childList: true,
        subtree: true,
      });

      setTimeout(() => {
        observer.disconnect();
        resolve(false);
      }, 2000);
    });
  }

  /**
   * Reset to initial state (called after facet updates)
   */
  async reset() {
    if (this.abortController) {
      this.abortController.abort();
    }

    this.currentPage = 1;
    this.isLoading = false;
    this.isFetching = false;
    this.loadedProductIds.clear();
    this.hideLoading();

    const reconnected = await this.reconnectDOM();

    if (!reconnected) {
      this.isEnabled = false;
      this.showPagination();
      return;
    }

    this.trackInitialProducts();

    this.extractPaginationInfo();
    this.hidePagination();

    if (this.endMessage) {
      this.endMessage.style.display = 'none';
    }

    if (this.hasMorePages) {
      this.isEnabled = true;
    }
  }

  /**
   * Show loading
   */
  showLoading() {
    if (!this.loadingIndicator || !document.body.contains(this.loadingIndicator)) {
      this.reconnectDOM();
    }

    if (this.loadingIndicator) {
      this.loadingIndicator.style.display = 'flex';
      const spinner = this.loadingIndicator.querySelector('.loading__spinner');
      if (spinner) {
        spinner.classList.remove('hidden');
      }
    }
  }

  /**
   * Hide loading
   */
  hideLoading() {
    if (!this.loadingIndicator || !document.body.contains(this.loadingIndicator)) {
      this.reconnectDOM();
    }

    if (this.loadingIndicator) {
      this.loadingIndicator.style.display = 'none';
      const spinner = this.loadingIndicator.querySelector('.loading__spinner');
      if (spinner) {
        spinner.classList.add('hidden');
      }
    }
  }

  /**
   * Show error state
   */
  showError(message = 'Error loading more products') {
    if (this.loadingIndicator) {
      this.loadingIndicator.innerHTML = `
        <div class="infinite-scroll-error">
          <p>${message}</p>
          <button type="button" class="button button--secondary" onclick="window.infiniteScroll.retry()">
            Try again
          </button>
        </div>
      `;
      this.loadingIndicator.style.display = 'flex';
    }
  }

  /**
   * Retry loading
   */
  retry() {
    this.isLoading = false;
    this.isFetching = false;
    this.hideLoading();

    if (this.loadingIndicator) {
      this.loadingIndicator.innerHTML = `
        <div class="loading__spinner">
          <svg aria-hidden="true" focusable="false" class="spinner" viewBox="0 0 66 66" xmlns="http://www.w3.org/2000/svg">
            <circle class="path" fill="none" stroke-width="6" cx="33" cy="33" r="30"></circle>
          </svg>
        </div>
      `;
    }

    this.loadNextPage();
  }

  /**
   * Destroy infinite scroll
   */
  destroy() {
    this.isEnabled = false;

    if (this.abortController) {
      this.abortController.abort();
    }

    if (this.gridObserver) {
      this.gridObserver.disconnect();
    }

    this.detachScrollListener();
    this.showPagination();
    this.hideLoading();

    if (this.endMessage) {
      this.endMessage.style.display = 'none';
    }
  }

  /**
   * Debounce helper
   */
  debounce(func, wait) {
    let timeout;
    const context = this;
    return function executedFunction(...args) {
      const later = () => {
        clearTimeout(timeout);
        func.apply(context, args);
      };
      clearTimeout(timeout);
      timeout = setTimeout(later, wait);
    };
  }
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    window.infiniteScroll = new InfiniteScroll();
  });
} else {
  window.infiniteScroll = new InfiniteScroll();
}
